'use client';

import { useEffect } from 'react';
import { useModal } from '@ebay/nice-modal-react';
import { usePreservedCallback } from '@toss/react';
import { isNil } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useDialog } from '@zep/dialog';
import { PaypleProvider, usePayple } from '../hooks';
import { AdditionalPaymentFormProvider } from '../paymentForm/AdditionalPaymentFormProvider';
import { InputCardNameModal } from '../paymentForm/InputCardNameModal';
import { PlanTitle } from './atoms';
import { PaymentLoading, PaymentPaypleCardRegister, PaymentPeriodRadio, PaymentSummary, PlanCheckoutSpace, PlanSelectMaxCcu, SpacePlanSelectButtons, SubscriptionPaymentSteps } from './components';
import { useSubscriptionPaymentContext } from './hooks';
import S from './SubscriptionPayment.module.scss';
import { SubscriptionPaymentProvider, SubscriptionPaymentProviderProps } from './SubscriptionPaymentProvider';
export const Content = () => {
  const dialog = useDialog();
  const {
    t
  } = useTranslation(['subscription']);
  const {
    isSuccess,
    isFailure,
    failMessage
  } = usePayple();
  const inputCardNameModal = useModal(InputCardNameModal);
  const {
    spaceHashId,
    handleIsRegisterCard,
    handleStep,
    step,
    handleSpaceBilling
  } = useSubscriptionPaymentContext();
  const onSuccessRegisterBilling = usePreservedCallback(() => {
    handleStep(2);
    inputCardNameModal.show({
      spaceHashId,
      onSuccess(cardName) {
        handleSpaceBilling({
          cardName
        });
        handleIsRegisterCard(true);
        inputCardNameModal.remove();
      }
    });
  });
  const onFailRegisterBilling = usePreservedCallback((message: string) => {
    handleStep(2);
    dialog.alert({
      title: t('subscription:payment.alertFailCardRegister.title'),
      content: t('subscription:payment.alertFailPay.descriptionTossPayment', {
        tosspaymentsErrorMessage: message ?? 'Processing Failure'
      })
    });
  });
  useEffect(() => {
    if (isSuccess) {
      onSuccessRegisterBilling();
    }
  }, [isSuccess, onSuccessRegisterBilling]);
  useEffect(() => {
    if (isFailure) {
      onFailRegisterBilling(failMessage);
      return;
    }
  }, [isFailure, failMessage, onFailRegisterBilling]);
  return <>
      <PaymentLoading data-sentry-element="PaymentLoading" data-sentry-source-file="SubscriptionForeignPayment.tsx" />
      <div className={S.wrapper}>
        <div className={S.container}>
          <div className={S.content}>
            <PlanTitle data-sentry-element="PlanTitle" data-sentry-source-file="SubscriptionForeignPayment.tsx" />
            <SubscriptionPaymentSteps data-sentry-element="SubscriptionPaymentSteps" data-sentry-source-file="SubscriptionForeignPayment.tsx" />
            {step === 1 ? <Step1 /> : <Step2 />}
          </div>
          <div className={S.action}>
            <PaymentSummary data-sentry-element="PaymentSummary" data-sentry-source-file="SubscriptionForeignPayment.tsx" />
          </div>
        </div>
      </div>
    </>;
};
const Step1 = () => {
  return <>
      <PaymentPeriodRadio data-sentry-element="PaymentPeriodRadio" data-sentry-source-file="SubscriptionForeignPayment.tsx" />
      <PlanSelectMaxCcu data-sentry-element="PlanSelectMaxCcu" data-sentry-source-file="SubscriptionForeignPayment.tsx" />
      <SpacePlanSelectButtons data-sentry-element="SpacePlanSelectButtons" data-sentry-source-file="SubscriptionForeignPayment.tsx" />
      <PlanCheckoutSpace data-sentry-element="PlanCheckoutSpace" data-sentry-source-file="SubscriptionForeignPayment.tsx" />
    </>;
};
const Step2 = () => <PaymentPaypleCardRegister data-sentry-element="PaymentPaypleCardRegister" data-sentry-component="Step2" data-sentry-source-file="SubscriptionForeignPayment.tsx" />;
export const SubscriptionForeignPayment = (props: SubscriptionPaymentProviderProps) => {
  return <PaypleProvider data-sentry-element="PaypleProvider" data-sentry-component="SubscriptionForeignPayment" data-sentry-source-file="SubscriptionForeignPayment.tsx">
      <SubscriptionPaymentProvider {...props} data-sentry-element="SubscriptionPaymentProvider" data-sentry-source-file="SubscriptionForeignPayment.tsx">
        <AdditionalPaymentFormProvider zipCode={isNil(props.billingInfo?.zipCode) ? null : props.billingInfo?.zipCode.toString()} data-sentry-element="AdditionalPaymentFormProvider" data-sentry-source-file="SubscriptionForeignPayment.tsx">
          <Content data-sentry-element="Content" data-sentry-source-file="SubscriptionForeignPayment.tsx" />
        </AdditionalPaymentFormProvider>
      </SubscriptionPaymentProvider>
    </PaypleProvider>;
};